import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn, Image as ImageIcon, Play } from 'lucide-react';

interface ImageGalleryProps {
  images: string[];
  alt: string;
  videoUrl?: string;
}

type MediaItem =
  | { type: 'image'; url: string }
  | { type: 'video'; url: string };

export function ImageGallery({ images, alt, videoUrl }: ImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Build unified media array: video first (if exists), then images
  const mediaItems: MediaItem[] = useMemo(() => {
    const items: MediaItem[] = [];
    if (videoUrl) {
      items.push({ type: 'video', url: videoUrl });
    }
    if (images && images.length > 0) {
      images.forEach(url => items.push({ type: 'image', url }));
    }
    return items;
  }, [images, videoUrl]);

  const hasMedia = mediaItems.length > 0;
  const currentItem = mediaItems[selectedIndex];
  const isCurrentVideo = currentItem?.type === 'video';

  if (!hasMedia) {
    return (
      <div className="aspect-[4/3] bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center text-gray-400">
          <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <span className="text-sm">No image available</span>
        </div>
      </div>
    );
  }

  const handlePrevious = () => {
    setSelectedIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      {/* Main Display */}
      <div className="relative group">
        <div className="aspect-[4/3] overflow-hidden rounded-lg bg-gray-100">
          {isCurrentVideo ? (
            // Video display with play button overlay
            <button
              className="relative w-full h-full"
              onClick={() => setIsLightboxOpen(true)}
            >
              <video
                src={currentItem.url}
                className="w-full h-full object-cover"
                muted
                playsInline
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                <div className="w-20 h-20 bg-hul-red rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Play className="w-10 h-10 text-white ml-1" fill="white" />
                </div>
              </div>
            </button>
          ) : (
            // Image display
            <motion.img
              key={selectedIndex}
              src={currentItem.url}
              alt={`${alt} - Image ${selectedIndex + 1}`}
              className="w-full h-full object-cover cursor-zoom-in"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsLightboxOpen(true)}
            />
          )}
        </div>

        {/* Zoom/Play Button */}
        <button
          onClick={() => setIsLightboxOpen(true)}
          className="absolute top-3 right-3 w-10 h-10 bg-black/50 text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
        >
          {isCurrentVideo ? <Play className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
        </button>

        {/* Navigation Arrows (if multiple media items) */}
        {mediaItems.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {mediaItems.length > 1 && (
        <div className="flex gap-2 mt-3">
          {mediaItems.map((item, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={`relative w-16 h-12 rounded overflow-hidden transition-all ${
                index === selectedIndex
                  ? 'ring-2 ring-hul-red ring-offset-2'
                  : 'opacity-60 hover:opacity-100'
              }`}
            >
              {item.type === 'video' ? (
                // Video thumbnail
                <>
                  <video
                    src={item.url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Play className="w-4 h-4 text-white" fill="white" />
                  </div>
                </>
              ) : (
                // Image thumbnail
                <img
                  src={item.url}
                  alt={`${alt} thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsLightboxOpen(false)}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsLightboxOpen(false)}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 text-white rounded-full flex items-center justify-center hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Navigation */}
            {mediaItems.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevious();
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 text-white rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNext();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 text-white rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            {/* Content - Video or Image */}
            {isCurrentVideo ? (
              <motion.video
                key={`video-${selectedIndex}`}
                src={currentItem.url}
                className="max-w-[90vw] max-h-[85vh] rounded-lg"
                controls
                autoPlay
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <motion.img
                key={`image-${selectedIndex}`}
                src={currentItem.url}
                alt={alt}
                className="max-w-[90vw] max-h-[85vh] object-contain"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {/* Counter */}
            {mediaItems.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 rounded-full text-white text-sm flex items-center gap-2">
                {isCurrentVideo && <Play className="w-3 h-3" fill="white" />}
                {selectedIndex + 1} / {mediaItems.length}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
